
import { useState } from 'react';

interface Customer {
  id: string;
  name: string;
  email: string;
  phone: string;
  company: string;
  website_type: string;
  status: 'hot' | 'following' | 'passive';
  created_at: string;
  last_contact: string;
  notes: string;
}

const AdminCustomers = () => {
  const [customers] = useState<Customer[]>([
    {
      id: '1',
      name: 'Ahmet Yılmaz',
      email: 'ahmet@example.com',
      phone: '0532 123 45 67',
      company: 'ABC Teknoloji',
      website_type: 'E-ticaret',
      status: 'hot',
      created_at: '2025-01-05',
      last_contact: '2025-01-08',
      notes: 'E-ticaret sitesi için demo talep etti. Çok ilgili.'
    },
    {
      id: '2',
      name: 'Fatma Kaya',
      email: 'fatma@example.com',
      phone: '0533 234 56 78',
      company: 'Sağlık Merkezi',
      website_type: 'Kurumsal',
      status: 'following',
      created_at: '2025-01-03',
      last_contact: '2025-01-07',
      notes: 'Kurumsal web sitesi istiyor. Fiyat bilgisi istedi.'
    },
    {
      id: '3',
      name: 'Mehmet Demir',
      email: 'mehmet@example.com',
      phone: '0534 345 67 89',
      company: 'Eğitim Akademisi',
      website_type: 'Eğitim',
      status: 'passive',
      created_at: '2024-12-28',
      last_contact: '2025-01-02',
      notes: 'Online eğitim platformu istiyor ama bütçe sınırlı.'
    },
    {
      id: '4',
      name: 'Ayşe Öztürk',
      email: 'ayse@example.com',
      phone: '0535 456 78 90',
      company: 'Teknoloji Şirketi',
      website_type: 'Teknoloji',
      status: 'hot',
      created_at: '2025-01-06',
      last_contact: '2025-01-08',
      notes: 'Startup için modern web sitesi istiyor. Hızlı karar verebilir.'
    },
    {
      id: '5',
      name: 'Can Arslan',
      email: 'can@example.com',
      phone: '0536 567 89 01',
      company: 'Turizm Acentesi',
      website_type: 'Turizm',
      status: 'following',
      created_at: '2025-01-04',
      last_contact: '2025-01-06',
      notes: 'Rezervasyon sistemi entegreli site istiyor.'
    }
  ]);

  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    website_type: 'all'
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'hot': return 'bg-red-100 text-red-800';
      case 'following': return 'bg-yellow-100 text-yellow-800';
      case 'passive': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'hot': return '🔥 Sıcak';
      case 'following': return '👀 Takipte';
      case 'passive': return '😴 Pasif';
      default: return 'Bilinmiyor';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'hot': return 'ri-fire-line';
      case 'following': return 'ri-eye-line';
      case 'passive': return 'ri-pause-circle-line';
      default: return 'ri-question-line';
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesStatus = filters.status === 'all' || customer.status === filters.status;
    const matchesSearch = !filters.search || 
      customer.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      customer.email.toLowerCase().includes(filters.search.toLowerCase()) ||
      customer.company.toLowerCase().includes(filters.search.toLowerCase());
    const matchesType = filters.website_type === 'all' || customer.website_type === filters.website_type;
    
    return matchesStatus && matchesSearch && matchesType;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Müşteri Listesi</h1>
            <p className="text-gray-600 mt-1">Tüm müşterilerinizi buradan yönetin</p>
          </div>
          <div className="flex items-center space-x-3">
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
              {filteredCustomers.length} müşteri
            </span>
            <button
              onClick={() => setShowAddModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-add-line mr-2"></i>
              Yeni Müşteri Ekle
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Arama</label>
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Ad, e-posta, şirket ara..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tüm Durumlar</option>
              <option value="hot">🔥 Sıcak</option>
              <option value="following">👀 Takipte</option>
              <option value="passive">😴 Pasif</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Web Sitesi Türü</label>
            <select
              value={filters.website_type}
              onChange={(e) => setFilters(prev => ({ ...prev, website_type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Tüm Türler</option>
              <option value="E-ticaret">E-ticaret</option>
              <option value="Kurumsal">Kurumsal</option>
              <option value="Eğitim">Eğitim</option>
              <option value="Teknoloji">Teknoloji</option>
              <option value="Turizm">Turizm</option>
            </select>
          </div>
        </div>
      </div>

      {/* Customers Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCustomers.map((customer) => (
          <div key={customer.id} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
            {/* Customer Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                  <span className="text-white font-semibold text-lg">
                    {customer.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{customer.name}</h3>
                  <p className="text-sm text-gray-600">{customer.company}</p>
                </div>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(customer.status)}`}>
                {getStatusText(customer.status)}
              </span>
            </div>

            {/* Customer Info */}
            <div className="space-y-3 mb-4">
              <div className="flex items-center text-sm text-gray-600">
                <i className="ri-mail-line mr-2 w-4 h-4"></i>
                <span className="truncate">{customer.email}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <i className="ri-phone-line mr-2 w-4 h-4"></i>
                <span>{customer.phone}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <i className="ri-global-line mr-2 w-4 h-4"></i>
                <span>{customer.website_type}</span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <i className="ri-calendar-line mr-2 w-4 h-4"></i>
                <span>Son iletişim: {new Date(customer.last_contact).toLocaleDateString('tr-TR')}</span>
              </div>
            </div>

            {/* Notes */}
            {customer.notes && (
              <div className="mb-4">
                <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 line-clamp-2">
                  {customer.notes}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex space-x-2">
              <button
                onClick={() => setSelectedCustomer(customer)}
                className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors text-sm font-medium whitespace-nowrap"
              >
                <i className="ri-eye-line mr-1"></i>
                Detay
              </button>
              <a
                href={`mailto:${customer.email}`}
                className="flex-1 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium text-center whitespace-nowrap"
              >
                <i className="ri-mail-line mr-1"></i>
                Mesaj
              </a>
              <button className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors whitespace-nowrap">
                <i className="ri-more-line"></i>
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredCustomers.length === 0 && (
        <div className="bg-white rounded-2xl p-12 shadow-lg border border-gray-100 text-center">
          <i className="ri-team-line text-4xl text-gray-400 mb-4"></i>
          <p className="text-gray-600">Henüz müşteri bulunmuyor</p>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">Yeni Müşteri Ekle</h2>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              <form className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Müşteri adı"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="ornek@email.com"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                  <input
                    type="tel"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0532 123 45 67"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Şirket Adı</label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Şirket adı"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Web Sitesi Türü</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="">Seçiniz</option>
                    <option value="E-ticaret">E-ticaret</option>
                    <option value="Kurumsal">Kurumsal</option>
                    <option value="Eğitim">Eğitim</option>
                    <option value="Teknoloji">Teknoloji</option>
                    <option value="Turizm">Turizm</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
                  <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                    <option value="hot">🔥 Sıcak</option>
                    <option value="following">👀 Takipte</option>
                    <option value="passive">😴 Pasif</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notlar</label>
                  <textarea
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Müşteri hakkında notlar..."
                  ></textarea>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    İptal
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300 whitespace-nowrap"
                  >
                    Ekle
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedCustomer.name} - Müşteri Detayları
                </h2>
                <button
                  onClick={() => setSelectedCustomer(null)}
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              <div className="space-y-6">
                {/* Müşteri Bilgileri */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Müşteri Bilgileri</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ad Soyad</label>
                      <p className="text-gray-900">{selectedCustomer.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
                      <p className="text-gray-900">{selectedCustomer.email}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                      <p className="text-gray-900">{selectedCustomer.phone}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Şirket</label>
                      <p className="text-gray-900">{selectedCustomer.company}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Web Sitesi Türü</label>
                      <p className="text-gray-900">{selectedCustomer.website_type}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Durum</label>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedCustomer.status)}`}>
                        {getStatusText(selectedCustomer.status)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Notlar */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Notlar</h3>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <p className="text-gray-700">{selectedCustomer.notes}</p>
                  </div>
                </div>

                {/* İletişim Geçmişi */}
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3">Son Görüşmeler</h3>
                  <div className="space-y-3">
                    <div className="flex items-start space-x-3 p-3 bg-blue-50 rounded-lg">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-phone-line text-white text-sm"></i>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Telefon Görüşmesi</p>
                        <p className="text-sm text-gray-600">8 Ocak 2025 - 14:30</p>
                        <p className="text-sm text-gray-700 mt-1">Proje detayları konuşuldu. Fiyat teklifi gönderilecek.</p>
                      </div>
                    </div>
                    <div className="flex items-start space-x-3 p-3 bg-green-50 rounded-lg">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-mail-line text-white text-sm"></i>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">E-posta Gönderildi</p>
                        <p className="text-sm text-gray-600">5 Ocak 2025 - 10:15</p>
                        <p className="text-sm text-gray-700 mt-1">İlk iletişim kuruldu. Demo talep edildi.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* İletişim Butonları */}
                <div className="flex space-x-3 pt-4 border-t border-gray-200">
                  <a
                    href={`mailto:${selectedCustomer.email}`}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-center whitespace-nowrap"
                  >
                    <i className="ri-mail-line mr-2"></i>
                    E-posta Gönder
                  </a>
                  <a
                    href={`tel:${selectedCustomer.phone}`}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-center whitespace-nowrap"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Ara
                  </a>
                  <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors whitespace-nowrap">
                    <i className="ri-edit-line mr-2"></i>
                    Düzenle
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;
